import { useCallback, useEffect, useRef, useState } from 'react';
import type { BrandArtAvailability, BrandArtJob } from '@icetus/contracts';
import {
  ApiError,
  createBrandArtJob,
  fetchBrandArtAvailability,
  fetchBrandArtJob,
  type CreateArtJobInput,
} from '../api';

/**
 * 一条 AI 出图任务的完整生命周期：发起 → 轮询 → 拿到结果。
 *
 * ## 为什么抽出来
 *
 * 品牌标和分类封面是两个面板、两个出口，但"点一下 → 排队 → 每 2 秒问一次 →
 * 成了/败了"这段一模一样。写两遍的代价不是重复，是**改一处漏一处**：
 * 轮询停止条件、额度刷新时机、失败时的措辞，任何一条下次只改一边，
 * 两个面板就会对同一件事给出不同答案。
 *
 * ## 为什么不在这里管 `note`
 *
 * 面板要显示的不只是任务错误（还有"已填入表单""先生成一张"之类），
 * 混在一个 state 里就得约定优先级。这里只交 `error`，由面板决定怎么说。
 */

const POLL_INTERVAL_MS = 2_000;

export interface ArtJobController {
  /** 能不能生成 + 这个月还剩几张。第一次刷新没回来前是 null */
  available: BrandArtAvailability | null;
  job: BrandArtJob | null;
  /** 正在出图：刚点下去，或者任务还在 pending/running */
  running: boolean;
  /** 发起或查询失败时说给客户听的一句话 */
  error: string | null;
  /** 发起一次。失败写进 `error`，不抛 */
  start: (input: CreateArtJobInput) => Promise<void>;
  /** 清掉上一条任务与错误（换站点、重开面板时用） */
  reset: () => void;
  /** 重新拉一次额度。建任务之后立刻调：那一刻额度已经扣了 */
  refreshAvailability: () => Promise<void>;
}

/**
 * @param tenantId 租户。面板关着时传空串也能挂，只是不会发请求
 * @param enabled 面板开着才轮询/拉额度：关着的时候每 2 秒问一次纯属浪费
 */
export function useArtJob(tenantId: string, enabled: boolean): ArtJobController {
  const [available, setAvailable] = useState<BrandArtAvailability | null>(null);
  const [job, setJob] = useState<BrandArtJob | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // 卸载时一定要停：定时器里还在 `setJob`，不停就是一个永久的泄漏
  useEffect(() => stopPolling, [stopPolling]);

  const refreshAvailability = useCallback(async () => {
    if (!tenantId) return;
    try {
      setAvailable(await fetchBrandArtAvailability(tenantId));
    } catch {
      /**
       * 拉不到状态按"不可用"处理，但**别把 quota 编成 0**：
       * 界面上写"本月还剩 0 张"会让客户以为额度没了，其实只是接口没通。
       */
      setAvailable({
        available: false,
        reason: '查不到生成服务状态，稍后再试',
        quota: { limit: 0, used: 0, remaining: 0, resetsAt: new Date().toISOString() },
      });
    }
  }, [tenantId]);

  const reset = useCallback(() => {
    stopPolling();
    setJob(null);
    setError(null);
    setStarting(false);
  }, [stopPolling]);

  const start = useCallback(
    async (input: CreateArtJobInput) => {
      setStarting(true);
      setError(null);
      setJob(null);
      try {
        const created = await createBrandArtJob(tenantId, input);
        setJob(created);
        // 建任务当下就已经计入本月用量了（额度按行数算），界面要立刻跟上
        void refreshAvailability();
        stopPolling();
        timerRef.current = window.setInterval(() => {
          void fetchBrandArtJob(tenantId, created.id)
            .then((next) => {
              setJob(next);
              if (next.status === 'succeeded' || next.status === 'failed') {
                stopPolling();
                setStarting(false);
              }
            })
            .catch((err: unknown) => {
              stopPolling();
              setStarting(false);
              setError(`查不到生成进度：${err instanceof Error ? err.message : String(err)}`);
            });
        }, POLL_INTERVAL_MS);
      } catch (err) {
        setStarting(false);
        setError(err instanceof ApiError ? err.message : '发起生成失败，稍后再试');
        // 被额度拦下时额度数字已经变了，刷一次让界面说实话
        void refreshAvailability();
      }
    },
    [tenantId, refreshAvailability, stopPolling],
  );

  const running = starting || job?.status === 'pending' || job?.status === 'running';

  // 面板关掉就停轮询：客户已经不看它了，每 2 秒一次请求只是烧流量
  useEffect(() => {
    if (!enabled) stopPolling();
  }, [enabled, stopPolling]);

  return { available, job, running, error, start, reset, refreshAvailability };
}
